// Promotes the brand kit's `<link rel="preload" as="style">` (index.html) to a
// stylesheet once the kit has loaded. It lives here, not in an inline `onload`
// attribute: the deployed SPA CSP (`script-src 'self'`) blocks inline event
// handlers, which strands the link as a preload (specs/011-27 D8, FR-010).
export function promoteBrandKitPreload(doc: Document = document): void {
  const preload = doc.head.querySelector<HTMLLinkElement>('link[rel~="preload"][as="style"]');
  if (!preload) return;

  const promote = () => {
    preload.rel = "stylesheet";
  };

  // The preload may already have finished before this module ran (a cached
  // kit finishes before the entry module evaluates), in which case its `load`
  // event is gone. The resource-timing entry stands in for it; its status is
  // exposed because the link is fetched in CORS mode (`crossorigin`).
  const finished = performance
    .getEntriesByName(preload.href, "resource")
    .some((entry) => isLoadedEntry(entry as PerformanceResourceTiming));
  if (finished) {
    promote();
    return;
  }

  // Only a proven load promotes; `error` leaves the link as an inert preload
  // (FR-006: no promotion, retry or error surface for a failed kit).
  preload.addEventListener("load", promote, { once: true });
}

// Mirrors the browser's own preload `load`/`error` split where the entry says
// so: anything below 400 fires `load`; 0 is a network failure or an opaque
// response. WebKit (Safari desktop and iOS) exposes no `responseStatus` and
// reports every body size as 0 even under `Timing-Allow-Origin: *`, so there
// the entry's existence is the signal (research D8, validation/webkit-probe):
// an aborted fetch leaves no entry, and promoting a non-2xx response is inert —
// WebKit and Chromium both discard the body and issue no second request.
function isLoadedEntry({ responseStatus }: PerformanceResourceTiming): boolean {
  if (typeof responseStatus === "number") return responseStatus >= 200 && responseStatus < 400;
  return true;
}

// Runs on import: src/main.tsx lists this module as its first import, so the
// promotion happens before anything else in the bundle evaluates (FR-010).
promoteBrandKitPreload();
